import Text from '@shared/components/content/text';
import { Button } from '@shared/components/ui/button';
import { Card } from '@shared/components/ui/card';
import { getCurrencySymbol } from '@shared/lib/format';
import { formatNumber } from '@shared/lib/utils';
import { useNavigate } from 'react-router-dom';

interface ExcludedCategoryCardProps {
  /** Lucide icon element, e.g. `<Icons.help />`. */
  icon: React.ReactNode;
  title: string;
  description: string;
  count: number;
  /** Total spend. Hidden until available (summary endpoint not yet wired). */
  spend?: number;
  /** Currency for the spend figure. Only needed when `spend` is provided. */
  currency?: string | null;
  /** CTA label, e.g. "Review items" / "View items". */
  buttonLabel: string;
  /** Destination for the CTA. */
  to: string;
  /** Render the CTA as the teal primary action (otherwise transparent). */
  primary?: boolean;
}

/** A single big number + small unit label, e.g. "280 items". */
function CardStat({ value, unit }: { value: string; unit: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <Text className="text-xl font-medium text-primary-800">{value}</Text>
      <Text variant="subtle">{unit}</Text>
    </div>
  );
}

/**
 * A clickable summary card for one excluded-items category on the landing page:
 * icon + title, right-aligned count/spend stats, an explainer, and a CTA that
 * deep-links into the category's list. Shared by the purchased- and sold-goods
 * Excluded items hubs.
 */
export default function ExcludedCategoryCard({
  icon,
  title,
  description,
  count,
  spend,
  currency,
  buttonLabel,
  to,
  primary = false,
}: ExcludedCategoryCardProps) {
  const navigate = useNavigate();

  return (
    <Card className="flex flex-col gap-4 p-6">
      <div className="flex items-center justify-between gap-6">
        <div className="flex items-center gap-2">
          <span className="shrink-0 text-primary-500">{icon}</span>
          <Text variant="cardTitle" className="font-semibold text-primary-800">
            {title}
          </Text>
        </div>
        <div className="flex shrink-0 items-baseline gap-8">
          <CardStat
            value={count.toLocaleString('en-GB')}
            unit={count === 1 ? 'item' : 'items'}
          />
          {spend != null && (
            <CardStat
              value={`${getCurrencySymbol(currency)}${formatNumber(spend)}`}
              unit="spend"
            />
          )}
        </div>
      </div>
      <Text variant="subtle" className="max-w-xl">
        {description}
      </Text>
      <Button
        variant={primary ? 'default' : 'outline'}
        className="w-fit"
        disabled={count === 0}
        onClick={() => navigate(to)}
      >
        {buttonLabel}
      </Button>
    </Card>
  );
}
